import React, { useMemo, useState, createElement } from 'react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Search, Filter, Clock, CheckCircle, XCircle, AlertCircle, Calendar, MessageSquare, Image as ImageIcon, FileText, Edit, Building2, Send } from 'lucide-react';
type RequestStatus = 'pending' | 'in-progress' | 'approved' | 'rejected';
type RequestType = 'image' | 'details' | 'remove';
type Request = {
  id: string;
  builderId: string;
  builderName: string;
  projectName: string;
  optionName: string;
  category: string;
  type: RequestType;
  status: RequestStatus;
  description: string;
  submittedDate: string;
  updatedDate: string;
  adminNotes?: string;
};
const requests: Request[] = [{
  id: 'REQ-001',
  builderId: 'BLD-001',
  builderName: 'Mattamy Homes',
  projectName: 'Oakwood Estates',
  optionName: 'Black Stainless Fridge',
  category: 'Interior > Kitchen > Refrigerators',
  type: 'image',
  status: 'approved',
  description: 'Need updated product image with new model',
  submittedDate: '2024-01-20',
  updatedDate: '2024-01-22',
  adminNotes: 'Image updated successfully. New photo from manufacturer.'
}, {
  id: 'REQ-002',
  builderId: 'BLD-001',
  builderName: 'Mattamy Homes',
  projectName: 'Oakwood Estates',
  optionName: 'Gray Asphalt Shingles',
  category: 'Exterior > Roofing > Shingles',
  type: 'details',
  status: 'in-progress',
  description: 'Update warranty information from 25 to 30 years',
  submittedDate: '2024-01-21',
  updatedDate: '2024-01-21'
}, {
  id: 'REQ-003',
  builderId: 'BLD-002',
  builderName: 'Brookfield Residential',
  projectName: 'Riverside Commons',
  optionName: 'White Vinyl Siding',
  category: 'Exterior > Siding > Vinyl',
  type: 'remove',
  status: 'pending',
  description: 'Discontinued by manufacturer, need to remove from options',
  submittedDate: '2024-01-22',
  updatedDate: '2024-01-22'
}, {
  id: 'REQ-004',
  builderId: 'BLD-001',
  builderName: 'Mattamy Homes',
  projectName: 'Maple Residences',
  optionName: 'Natural Oak Flooring',
  category: 'Interior > Flooring > Hardwood',
  type: 'details',
  status: 'rejected',
  description: 'Change price from $8,500 to $9,200',
  submittedDate: '2024-01-19',
  updatedDate: '2024-01-20',
  adminNotes: 'Price increase not approved. Please provide manufacturer documentation.'
}, {
  id: 'REQ-005',
  builderId: 'BLD-003',
  builderName: 'Pulte Homes',
  projectName: 'Sunset Ridge',
  optionName: 'Built-in Fridge',
  category: 'Interior > Kitchen > Refrigerators',
  type: 'image',
  status: 'in-progress',
  description: 'Add lifestyle image showing fridge in kitchen setting',
  submittedDate: '2024-01-22',
  updatedDate: '2024-01-22'
}, {
  id: 'REQ-006',
  builderId: 'BLD-002',
  builderName: 'Brookfield Residential',
  projectName: 'Riverside Commons',
  optionName: 'Quartz Countertops',
  category: 'Interior > Kitchen > Countertops',
  type: 'details',
  status: 'pending',
  description: 'Update description to include new color options',
  submittedDate: '2024-01-23',
  updatedDate: '2024-01-23'
}];
const statusConfig = {
  pending: {
    label: 'Pending',
    variant: 'warning' as const,
    icon: Clock,
    color: 'text-amber-600'
  },
  'in-progress': {
    label: 'In Progress',
    variant: 'primary' as const,
    icon: AlertCircle,
    color: 'text-blue-600'
  },
  approved: {
    label: 'Completed',
    variant: 'success' as const,
    icon: CheckCircle,
    color: 'text-green-600'
  },
  rejected: {
    label: 'Rejected',
    variant: 'danger' as const,
    icon: XCircle,
    color: 'text-red-600'
  }
};
const typeConfig = {
  image: {
    label: 'Image Update',
    icon: ImageIcon,
    color: 'text-purple-600'
  },
  details: {
    label: 'Details Update',
    icon: Edit,
    color: 'text-blue-600'
  },
  remove: {
    label: 'Remove Option',
    icon: XCircle,
    color: 'text-red-600'
  }
};
export function AdminRequestsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RequestStatus>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | RequestType>('all');
  const [builderFilter, setBuilderFilter] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseData, setResponseData] = useState({
    status: '' as RequestStatus,
    notes: ''
  });
  // Get unique builders
  const builders = useMemo(() => {
    const uniqueBuilders = Array.from(new Set(requests.map(r => JSON.stringify({
      id: r.builderId,
      name: r.builderName
    })))).map(str => JSON.parse(str));
    return uniqueBuilders;
  }, []);
  // Filtered requests
  const filteredRequests = useMemo(() => {
    let filtered = requests;
    // Builder filter
    if (builderFilter !== 'all') {
      filtered = filtered.filter(r => r.builderId === builderFilter);
    }
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(r => r.type === typeFilter);
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => r.id.toLowerCase().includes(query) || r.builderName.toLowerCase().includes(query) || r.projectName.toLowerCase().includes(query) || r.optionName.toLowerCase().includes(query) || r.category.toLowerCase().includes(query) || r.description.toLowerCase().includes(query));
    }
    return filtered;
  }, [builderFilter, statusFilter, typeFilter, searchQuery]);
  // Stats
  const stats = useMemo(() => {
    const filtered = builderFilter === 'all' ? requests : requests.filter(r => r.builderId === builderFilter);
    return {
      total: filtered.length,
      pending: filtered.filter(r => r.status === 'pending').length,
      inProgress: filtered.filter(r => r.status === 'in-progress').length,
      approved: filtered.filter(r => r.status === 'approved').length,
      rejected: filtered.filter(r => r.status === 'rejected').length
    };
  }, [builderFilter]);
  const handleRespond = (request: Request) => {
    setSelectedRequest(request);
    setShowResponseModal(true);
    setResponseData({
      status: request.status === 'pending' ? 'in-progress' : request.status,
      notes: request.adminNotes || ''
    });
  };
  const handleSubmitResponse = () => {
    console.log('Submitting response:', {
      request: selectedRequest,
      response: responseData
    });
    setShowResponseModal(false);
    setSelectedRequest(null);
  };
  return <>
      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-363">
        <div className="flex items-center justify-between" data-id="element-364">
          <div data-id="element-365">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-366">
              All Change Requests
            </h1>
            <p className="text-slate-500" data-id="element-367">
              Review and manage requests from all builders
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-368">
          <Card noPadding data-id="element-369">
            <div className="p-4" data-id="element-370">
              <div className="flex items-center justify-between mb-2" data-id="element-371">
                <p className="text-sm text-slate-500" data-id="element-372">Total Requests</p>
                <FileText className="h-4 w-4 text-slate-400" data-id="element-373" />
              </div>
              <p className="text-2xl font-bold text-slate-900" data-id="element-374">{stats.total}</p>
            </div>
          </Card>

          <Card noPadding data-id="element-375">
            <div className="p-4" data-id="element-376">
              <div className="flex items-center justify-between mb-2" data-id="element-377">
                <p className="text-sm text-slate-500" data-id="element-378">Pending</p>
                <Clock className="h-4 w-4 text-amber-500" data-id="element-379" />
              </div>
              <p className="text-2xl font-bold text-amber-600" data-id="element-380">
                {stats.pending}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-381">
            <div className="p-4" data-id="element-382">
              <div className="flex items-center justify-between mb-2" data-id="element-383">
                <p className="text-sm text-slate-500" data-id="element-384">In Progress</p>
                <AlertCircle className="h-4 w-4 text-blue-500" data-id="element-385" />
              </div>
              <p className="text-2xl font-bold text-blue-600" data-id="element-386">
                {stats.inProgress}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-387">
            <div className="p-4" data-id="element-388">
              <div className="flex items-center justify-between mb-2" data-id="element-389">
                <p className="text-sm text-slate-500" data-id="element-390">Completed</p>
                <CheckCircle className="h-4 w-4 text-green-500" data-id="element-391" />
              </div>
              <p className="text-2xl font-bold text-green-600" data-id="element-392">
                {stats.approved}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-393">
            <div className="p-4" data-id="element-394">
              <div className="flex items-center justify-between mb-2" data-id="element-395">
                <p className="text-sm text-slate-500" data-id="element-396">Rejected</p>
                <XCircle className="h-4 w-4 text-red-500" data-id="element-397" />
              </div>
              <p className="text-2xl font-bold text-red-600" data-id="element-398">
                {stats.rejected}
              </p>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card noPadding data-id="element-399">
          <div className="p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl" data-id="element-400">
            <div className="flex items-center gap-3 mb-3" data-id="element-401">
              {/* Search */}
              <div className="flex-1 relative" data-id="element-402">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-403" />
                <input type="text" placeholder="Search by ID, builder, project, option, or description..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-404" />
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-405">
              {/* Builder Filter */}
              <select value={builderFilter} onChange={e => setBuilderFilter(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-406">
                <option value="all" data-id="element-407">All Builders</option>
                {builders.map(builder => <option key={builder.id} value={builder.id} data-id="element-408">
                    {builder.name}
                  </option>)}
              </select>

              {/* Status Filter */}
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-409">
                <option value="all" data-id="element-410">All Status</option>
                <option value="pending" data-id="element-411">Pending</option>
                <option value="in-progress" data-id="element-412">In Progress</option>
                <option value="approved" data-id="element-413">Completed</option>
                <option value="rejected" data-id="element-414">Rejected</option>
              </select>

              {/* Type Filter */}
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-415">
                <option value="all" data-id="element-416">All Types</option>
                <option value="image" data-id="element-417">Image Update</option>
                <option value="details" data-id="element-418">Details Update</option>
                <option value="remove" data-id="element-419">Remove Option</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-420">
            <table className="w-full text-left text-sm" data-id="element-421">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-422">
                <tr data-id="element-423">
                  <th className="px-6 py-4" data-id="element-424">Request ID</th>
                  <th className="px-6 py-4" data-id="element-425">Builder</th>
                  <th className="px-6 py-4" data-id="element-426">Project</th>
                  <th className="px-6 py-4" data-id="element-427">Option Name</th>
                  <th className="px-6 py-4" data-id="element-428">Type</th>
                  <th className="px-6 py-4" data-id="element-429">Status</th>
                  <th className="px-6 py-4" data-id="element-430">Submitted</th>
                  <th className="px-6 py-4 text-right" data-id="element-431">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-432">
                {filteredRequests.length === 0 ? <tr data-id="element-433">
                    <td colSpan={8} className="px-6 py-12 text-center text-slate-500" data-id="element-434">
                      No requests found matching your filters
                    </td>
                  </tr> : filteredRequests.map(request => {
                const StatusIcon = statusConfig[request.status].icon;
                const TypeIcon = typeConfig[request.type].icon;
                return <tr key={request.id} className="hover:bg-slate-50 transition-colors" data-id="element-435">
                        <td className="px-6 py-4 font-medium text-slate-900" data-id="element-436">
                          {request.id}
                        </td>
                        <td className="px-6 py-4" data-id="element-437">
                          <div className="flex items-center gap-2" data-id="element-438">
                            <Building2 className="h-4 w-4 text-slate-400" data-id="element-439" />
                            <span className="text-slate-700" data-id="element-440">
                              {request.builderName}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-441">
                          {request.projectName}
                        </td>
                        <td className="px-6 py-4" data-id="element-442">
                          <div className="font-medium text-slate-900" data-id="element-443">
                            {request.optionName}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-444">
                            {request.category}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-445">
                          <div className="flex items-center gap-2" data-id="element-446">
                            <TypeIcon className={`h-4 w-4 ${typeConfig[request.type].color}`} data-id="element-447" />
                            <span className="text-slate-700 text-xs" data-id="element-448">
                              {typeConfig[request.type].label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-449">
                          <Badge variant={statusConfig[request.status].variant} data-id="element-450">
                            <div className="flex items-center gap-1" data-id="element-451">
                              <StatusIcon className="h-3 w-3" data-id="element-452" />
                              {statusConfig[request.status].label}
                            </div>
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-453">
                          <div className="flex items-center gap-1" data-id="element-454">
                            <Calendar className="h-3 w-3 text-slate-400" data-id="element-455" />
                            {new Date(request.submittedDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-456">
                          <div className="flex items-center justify-end gap-2" data-id="element-457">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(request)} data-id="element-458">
                              View
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleRespond(request)} data-id="element-459">
                              Respond
                            </Button>
                          </div>
                        </td>
                      </tr>;
              })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Request Details Modal */}
      {selectedRequest && !showResponseModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-460">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-461">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white" data-id="element-462">
              <div data-id="element-463">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-464">
                  Request Details
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-465">
                  {selectedRequest.id}
                </p>
              </div>
              <button onClick={() => setSelectedRequest(null)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-466">
                <XCircle className="h-5 w-5" data-id="element-467" />
              </button>
            </div>

            <div className="p-6 space-y-6" data-id="element-468">
              {/* Status & Type */}
              <div className="flex items-center gap-3" data-id="element-469">
                <Badge variant={statusConfig[selectedRequest.status].variant} className="text-sm" data-id="element-470">
                  <div className="flex items-center gap-1" data-id="element-471">
                    {createElement(statusConfig[selectedRequest.status].icon, {
                  className: 'h-3 w-3'
                })}
                    {statusConfig[selectedRequest.status].label}
                  </div>
                </Badge>
                <Badge variant="neutral" className="text-sm" data-id="element-472">
                  <div className="flex items-center gap-1" data-id="element-473">
                    {createElement(typeConfig[selectedRequest.type].icon, {
                  className: 'h-3 w-3'
                })}
                    {typeConfig[selectedRequest.type].label}
                  </div>
                </Badge>
              </div>

              {/* Builder & Project Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-id="element-474">
                <div className="grid grid-cols-2 gap-4" data-id="element-475">
                  <div data-id="element-476">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-477">Builder</p>
                    <div className="flex items-center gap-2" data-id="element-478">
                      <Building2 className="h-4 w-4 text-slate-400" data-id="element-479" />
                      <p className="font-medium text-slate-900" data-id="element-480">
                        {selectedRequest.builderName}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-481">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-482">Project</p>
                    <p className="font-medium text-slate-900" data-id="element-483">
                      {selectedRequest.projectName}
                    </p>
                  </div>
                </div>
                <div data-id="element-484">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-485">Option Name</p>
                  <p className="font-medium text-slate-900" data-id="element-486">
                    {selectedRequest.optionName}
                  </p>
                </div>
                <div data-id="element-487">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-488">Category Path</p>
                  <p className="text-sm text-slate-700" data-id="element-489">
                    {selectedRequest.category}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div data-id="element-490">
                <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-491">
                  Description
                </p>
                <div className="bg-slate-50 rounded-lg p-4" data-id="element-492">
                  <p className="text-slate-700" data-id="element-493">
                    {selectedRequest.description}
                  </p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4" data-id="element-494">
                <div data-id="element-495">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-496">Submitted Date</p>
                  <div className="flex items-center gap-2 text-slate-700" data-id="element-497">
                    <Calendar className="h-4 w-4 text-slate-400" data-id="element-498" />
                    {new Date(selectedRequest.submittedDate).toLocaleDateString()}
                  </div>
                </div>
                <div data-id="element-499">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-500">Last Updated</p>
                  <div className="flex items-center gap-2 text-slate-700" data-id="element-501">
                    <Clock className="h-4 w-4 text-slate-400" data-id="element-502" />
                    {new Date(selectedRequest.updatedDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedRequest.adminNotes && <div data-id="element-503">
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2" data-id="element-504">
                    <MessageSquare className="h-4 w-4" data-id="element-505" />
                    Admin Notes
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-506">
                    <p className="text-sm text-blue-900" data-id="element-507">
                      {selectedRequest.adminNotes}
                    </p>
                  </div>
                </div>}
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-508">
              <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-509">
                Close
              </Button>
              <Button onClick={() => handleRespond(selectedRequest)} data-id="element-510">
                Respond to Request
              </Button>
            </div>
          </div>
        </div>}

      {/* Response Modal */}
      {showResponseModal && selectedRequest && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-511">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" data-id="element-512">
            <div className="p-6 border-b border-slate-200" data-id="element-513">
              <h2 className="text-xl font-bold text-slate-900" data-id="element-514">
                Respond to Request
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-515">
                {selectedRequest.id} - {selectedRequest.optionName}
              </p>
            </div>

            <div className="p-6 space-y-4" data-id="element-516">
              <div data-id="element-517">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-518">
                  Update Status
                </label>
                <select value={responseData.status} onChange={e => setResponseData({
              ...responseData,
              status: e.target.value as RequestStatus
            })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-519">
                  <option value="" data-id="element-520">Select status...</option>
                  <option value="in-progress" data-id="element-521">In Progress</option>
                  <option value="approved" data-id="element-522">Completed</option>
                  <option value="rejected" data-id="element-523">Rejected</option>
                </select>
              </div>

              <div data-id="element-524">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-525">
                  Admin Notes
                </label>
                <textarea value={responseData.notes} onChange={e => setResponseData({
              ...responseData,
              notes: e.target.value
            })} placeholder="Add notes for the builder..." className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" rows={4} data-id="element-526" />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-527">
                <p className="text-xs text-blue-800" data-id="element-528">
                  The builder will be notified of your response and can view
                  your notes.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-529">
              <Button variant="outline" onClick={() => setShowResponseModal(false)} data-id="element-530">
                Cancel
              </Button>
              <Button onClick={handleSubmitResponse} disabled={!responseData.status} leftIcon={<Send className="h-4 w-4" data-id="element-532" />} data-id="element-531">
                Send Response
              </Button>
            </div>
          </div>
        </div>}
    </>;
}